<script>
import BackofficeLayout from '@/Layouts/BackofficeLayout.vue';

export default {
  layout: BackofficeLayout,
};
</script>

<script setup>
import { ref } from 'vue';
import { Head, useForm, router } from '@inertiajs/vue3';
import { Radio, Plus, Search, Edit2, Trash2, Power } from '@lucide/vue';

const props = defineProps({
  operators: Array,
  filters: Object,
});

const search = ref(props.filters?.search || '');
const isModalOpen = ref(false);
const editingOperator = ref(null);

const form = useForm({
  code: '',
  name: '',
  display_name: '',
  description: '',
  icon: '',
  sort_order: 0,
  is_active: true,
});

function handleSearch() {
  router.get(route('admin.catalogue.operators.index'), { search: search.value }, { preserveState: true, replace: true });
}

function openCreateModal() {
  editingOperator.value = null;
  form.reset();
  form.clearErrors();
  isModalOpen.value = true;
}

function openEditModal(op) {
  editingOperator.value = op;
  form.code = op.code;
  form.name = op.name;
  form.display_name = op.display_name || op.name;
  form.description = op.description || '';
  form.icon = op.icon || '';
  form.sort_order = op.sort_order;
  form.is_active = op.is_active;
  form.clearErrors();
  isModalOpen.value = true;
}

function saveOperator() {
  if (editingOperator.value) {
    form.put(route('admin.catalogue.operators.update', editingOperator.value.id), {
      onSuccess: () => {
        isModalOpen.value = false;
      },
    });
  } else {
    form.post(route('admin.catalogue.operators.store'), {
      onSuccess: () => {
        isModalOpen.value = false;
      },
    });
  }
}

function toggleActive(op) {
  router.patch(route('admin.catalogue.operators.toggle-active', op.id));
}

function deleteOperator(op) {
  if (confirm(`Apakah Anda yakin ingin menghapus operator '${op.name}'?`)) {
    router.delete(route('admin.catalogue.operators.destroy', op.id));
  }
}
</script>

<template>
  <Head title="Manajemen Operator Seluler" />

  <div class="space-y-6">
    <!-- Header -->
    <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
      <div>
        <h1 class="text-xl font-bold text-slate-900 flex items-center gap-2">
          <Radio class="w-5 h-5 text-indigo-600" />
          <span>Operator Seluler Master</span>
        </h1>
        <p class="text-xs text-slate-500 mt-0.5">Kelola data master provider seluler (Telkomsel, Indosat, XL, dll)</p>
      </div>

      <button
        @click="openCreateModal"
        type="button"
        class="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs shadow-sm transition"
      >
        <Plus class="w-4 h-4" />
        <span>Tambah Operator</span>
      </button>
    </div>

    <!-- Filter & Search Bar -->
    <div class="p-4 rounded-2xl bg-white border border-slate-200 shadow-xs flex items-center gap-3">
      <div class="relative flex-1">
        <Search class="w-4 h-4 text-slate-400 absolute left-3.5 top-3" />
        <input
          v-model="search"
          @keyup.enter="handleSearch"
          type="text"
          placeholder="Cari kode atau nama operator..."
          class="w-full pl-10 pr-4 py-2 text-xs rounded-xl border border-slate-300 focus:border-indigo-600 focus:ring-1 focus:ring-indigo-600 outline-none"
        />
      </div>
      <button @click="handleSearch" type="button" class="px-4 py-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-bold transition">
        Cari
      </button>
    </div>

    <!-- Operators Table -->
    <div class="bg-white rounded-2xl border border-slate-200 shadow-xs overflow-hidden">
      <div class="overflow-x-auto">
        <table class="w-full text-left border-collapse text-xs">
          <thead>
            <tr class="bg-slate-50 border-b border-slate-200 text-slate-500 font-bold uppercase tracking-wider text-[10px]">
              <th class="py-3 px-4">Kode</th>
              <th class="py-3 px-4">Nama Provider</th>
              <th class="py-3 px-4">Display Name</th>
              <th class="py-3 px-4 text-center">Jml Prefix</th>
              <th class="py-3 px-4 text-center">Jml Produk</th>
              <th class="py-3 px-4 text-center">Urutan</th>
              <th class="py-3 px-4 text-center">Status</th>
              <th class="py-3 px-4 text-right">Aksi</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-slate-100 font-medium text-slate-800">
            <tr v-for="op in operators" :key="op.id" class="hover:bg-slate-50/80 transition">
              <td class="py-3 px-4 font-mono font-bold text-indigo-600">{{ op.code }}</td>
              <td class="py-3 px-4 font-bold text-slate-900">{{ op.name }}</td>
              <td class="py-3 px-4 text-slate-600">{{ op.display_name || '-' }}</td>
              <td class="py-3 px-4 text-center font-bold text-slate-700">{{ op.prefixes_count }}</td>
              <td class="py-3 px-4 text-center font-bold text-slate-700">{{ op.products_count }}</td>
              <td class="py-3 px-4 text-center font-mono">{{ op.sort_order }}</td>
              <td class="py-3 px-4 text-center">
                <span
                  class="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold"
                  :class="op.is_active ? 'bg-emerald-100 text-emerald-800' : 'bg-rose-100 text-rose-800'"
                >
                  {{ op.is_active ? 'Aktif' : 'Nonaktif' }}
                </span>
              </td>
              <td class="py-3 px-4 text-right space-x-1">
                <button
                  @click="toggleActive(op)"
                  title="Toggle Aktif"
                  type="button"
                  class="p-1.5 rounded-lg border border-slate-200 hover:bg-slate-100 text-slate-600 transition"
                >
                  <Power class="w-3.5 h-3.5" :class="op.is_active ? 'text-emerald-600' : 'text-slate-400'" />
                </button>
                <button
                  @click="openEditModal(op)"
                  title="Edit Operator"
                  type="button"
                  class="p-1.5 rounded-lg border border-slate-200 hover:bg-slate-100 text-indigo-600 transition"
                >
                  <Edit2 class="w-3.5 h-3.5" />
                </button>
                <button
                  @click="deleteOperator(op)"
                  title="Hapus Operator"
                  type="button"
                  class="p-1.5 rounded-lg border border-slate-200 hover:bg-rose-50 text-rose-600 transition"
                >
                  <Trash2 class="w-3.5 h-3.5" />
                </button>
              </td>
            </tr>
            <tr v-if="operators.length === 0">
              <td colspan="8" class="py-8 text-center text-slate-400 italic">Belum ada data operator seluler.</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Modal Form Operator -->
    <div v-if="isModalOpen" class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-xs">
      <div class="bg-white rounded-3xl p-6 max-w-md w-full shadow-xl space-y-5">
        <h3 class="text-base font-bold text-slate-900">
          {{ editingOperator ? 'Edit Operator Seluler' : 'Tambah Operator Seluler' }}
        </h3>

        <form @submit.prevent="saveOperator" class="space-y-4 text-xs">
          <div>
            <label class="block font-semibold text-slate-700 mb-1">Kode Operator <span class="text-rose-500">*</span></label>
            <input
              v-model="form.code"
              type="text"
              placeholder="Contoh: TELKOMSEL"
              class="w-full px-3.5 py-2.5 rounded-xl border border-slate-300 font-mono font-bold uppercase focus:border-indigo-600 outline-none"
            />
            <span v-if="form.errors.code" class="text-rose-500 text-[11px] block mt-0.5">{{ form.errors.code }}</span>
          </div>

          <div>
            <label class="block font-semibold text-slate-700 mb-1">Nama Provider <span class="text-rose-500">*</span></label>
            <input
              v-model="form.name"
              type="text"
              placeholder="Contoh: Telkomsel"
              class="w-full px-3.5 py-2.5 rounded-xl border border-slate-300 focus:border-indigo-600 outline-none"
            />
            <span v-if="form.errors.name" class="text-rose-500 text-[11px] block mt-0.5">{{ form.errors.name }}</span>
          </div>

          <div>
            <label class="block font-semibold text-slate-700 mb-1">Display Name Customer</label>
            <input
              v-model="form.display_name"
              type="text"
              placeholder="Contoh: Telkomsel"
              class="w-full px-3.5 py-2.5 rounded-xl border border-slate-300 focus:border-indigo-600 outline-none"
            />
          </div>

          <div class="grid grid-cols-2 gap-3">
            <div>
              <label class="block font-semibold text-slate-700 mb-1">Urutan</label>
              <input
                v-model="form.sort_order"
                type="number"
                class="w-full px-3.5 py-2.5 rounded-xl border border-slate-300 focus:border-indigo-600 outline-none"
              />
            </div>

            <div class="flex items-center pt-6">
              <label class="inline-flex items-center gap-2 cursor-pointer">
                <input v-model="form.is_active" type="checkbox" class="w-4 h-4 rounded text-indigo-600 focus:ring-indigo-500" />
                <span class="font-bold text-slate-700">Status Aktif</span>
              </label>
            </div>
          </div>

          <div class="pt-2 flex justify-end gap-3">
            <button
              @click="isModalOpen = false"
              type="button"
              class="px-4 py-2.5 rounded-xl border border-slate-300 text-slate-700 font-bold transition"
            >
              Batal
            </button>
            <button
              :disabled="form.processing"
              type="submit"
              class="px-5 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-bold transition"
            >
              Simpan
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>
